import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, ExternalLink, FileDown, Mail, MailCheck, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const deDate = (s) => (s && s.length >= 10 ? s.slice(0, 10).split("-").reverse().join(".") : "—");

const STATUS = {
  offen: ["bg-blue-50 text-blue-900 border-blue-200", "Offen"],
  rueckmeldung: ["bg-amber-50 text-amber-900 border-amber-300", "Änderungswunsch"],
  processing: ["bg-amber-50 text-amber-900 border-amber-200", "Wird erstellt"],
  sent: ["bg-blue-50 text-blue-900 border-blue-200", "Versendet"],
  send_failed: ["bg-amber-50 text-amber-900 border-amber-200", "Versand fehlgeschlagen"],
  accepted: ["bg-emerald-50 text-emerald-900 border-emerald-200", "Angenommen"],
  angenommen: ["bg-emerald-50 text-emerald-900 border-emerald-200", "Angenommen"],
  declined: ["bg-red-50 text-red-900 border-red-200", "Abgelehnt"],
  abgelehnt: ["bg-red-50 text-red-900 border-red-200", "Abgelehnt"],
  expired: ["bg-[#F3EFE6] text-[#6B6459] border-[#DED6C6]", "Abgelaufen"],
  cancelled: ["bg-[#F3EFE6] text-[#6B6459] border-[#DED6C6]", "Storniert"],
};

const Badge = ({ status, abgelaufen }) => {
  const key = abgelaufen && status === "offen" ? "expired" : status;
  const [cls, label] = STATUS[key] || ["bg-[#F3EFE6] text-[#6B6459] border-[#DED6C6]", status || "—"];
  return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${cls}`}>{label}</span>;
};

const QUELLE = {
  angebotsseite: ["Angebotsseite", "angebotsseiten"],
  paket: ["Paket-Angebot", "quotes"],
  stunden: ["Stunden-Angebot", "quotes"],
};

const Aktion = ({ icon: Icon, onClick, href, children, testId, busy }) => {
  const cls = "inline-flex items-center gap-1.5 border border-[#E3DECE] bg-white hover:border-[#BA6D52] " +
    "text-[10px] uppercase tracking-[0.15em] text-[#575249] px-3 py-2 transition-colors disabled:opacity-50";
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls}>
        <Icon size={13} className="text-[#BA6D52]" />{children}
      </a>
    : <button onClick={onClick} disabled={busy} data-testid={testId} className={cls}>
        <Icon size={13} className="text-[#BA6D52]" />{children}
      </button>;
};

// Alle Angebote aus allen drei Quellen in einer Liste - mit denselben Aktionen an einem Ort
export const AngebotUebersicht = ({ pwd, onOpenTab }) => {
  const [rows, setRows] = useState(null);
  const [suche, setSuche] = useState("");
  const [filter, setFilter] = useState("alle");
  const [offen, setOffen] = useState(null);
  const [busy, setBusy] = useState(false);

  const headers = useMemo(() => ({ "X-Admin-Password": pwd }), [pwd]);

  const load = useCallback(async () => {
    const get = async (pfad) => {
      try { return (await axios.get(`${API}/${pfad}`, { headers })).data || []; }
      catch { return []; }
    };
    const [seiten, quotes, stunden] = await Promise.all([
      get("admin/angebotsseiten"), get("admin/quotes"), get("admin/rate-card-quotes"),
    ]);
    const basis = window.location.origin;

    const alle = [
      ...seiten.map((s) => ({
        id: s.id, quelle: "angebotsseite", nummer: s.nummer || "—",
        kunde: s.firma || s.ansprechpartner || "—", zusatz: s.anlass || "",
        ansprechpartner: s.ansprechpartner || "", email: s.email || "", ort: s.ort || "",
        personen: s.personen || 0,
        datum: s.datum, status: s.status, abgelaufen: s.abgelaufen,
        betrag: Number(s.summen?.brutto || s.summen?.netto || 0),
        gueltig: s.gueltig_bis, created: s.created_at || "",
        link: s.slug ? `${basis}/angebot-fuer/${s.slug}` : "",
      })),
      ...quotes.map((q) => ({
        id: q.id, quelle: "paket", nummer: q.quote_number || "—",
        kunde: q.betrieb || "—", zusatz: q.email || "",
        ansprechpartner: q.ansprechpartner || "", email: q.email || "", ort: q.ort || "",
        personen: q.personen || 0,
        datum: q.einsatzdatum, status: q.status, abgelaufen: false,
        betrag: (q.line_items || []).reduce(
          (a, it) => a + Number(it.quantity || 0) * Number(it.unit_price_net || 0) * (1 + Number(it.tax_rate ?? 19) / 100), 0),
        gueltig: q.valid_until, created: q.created_at || "",
        link: q.action_token ? `${basis}/angebot/${q.action_token}` : "",
      })),
      ...stunden.map((q) => ({
        id: q.id, quelle: "stunden", nummer: q.quote_number || "—",
        kunde: q.betrieb || "—", zusatz: q.ort || q.email || "",
        ansprechpartner: q.ansprechpartner || "", email: q.email || "", ort: q.ort || "",
        personen: 0,
        datum: "", status: q.status, abgelaufen: false,
        betrag: 0, gueltig: "", created: q.created_at || "",
        link: q.action_token ? `${basis}/stunden-angebot/${q.action_token}` : "",
      })),
    ].sort((a, b) => String(b.created).localeCompare(String(a.created)));

    setRows(alle);
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const pfadPdf = (r) => (r.quelle === "angebotsseite"
    ? `admin/angebotsseiten/${r.id}/pdf` : `admin/quotes/${r.id}/pdf`);

  const pdfOeffnen = async (r) => {
    setBusy(true);
    try {
      const { data: blob } = await axios.get(`${API}/${pfadPdf(r)}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("PDF konnte nicht geöffnet werden"); }
    finally { setBusy(false); }
  };

  const kopieAnMich = async (r) => {
    setBusy(true);
    try {
      const pfad = r.quelle === "angebotsseite"
        ? `admin/angebotsseiten/${r.id}/send-to-me` : `admin/quotes/${r.id}/send-to-me`;
      await axios.post(`${API}/${pfad}`, {}, { headers });
      toast.success("Kopie an dich verschickt");
    } catch (err) { toast.error(err?.response?.data?.detail || "Kopie konnte nicht verschickt werden"); }
    finally { setBusy(false); }
  };

  const anKundeSenden = async (r) => {
    if (!r.email) { toast.error("Für dieses Angebot ist keine E-Mail hinterlegt"); return; }
    setBusy(true);
    try {
      if (r.quelle === "angebotsseite") {
        await axios.post(`${API}/admin/angebotsseiten/${r.id}/send-email`, {}, { headers });
      } else {
        await axios.post(`${API}/admin/quotes/${r.id}/resend`, {}, { headers });
      }
      toast.success(`Angebot an ${r.email} verschickt`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Versand nicht möglich"); }
    finally { setBusy(false); }
  };

  const sichtbar = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (rows || []).filter((r) => {
      const istOffen = !["accepted", "angenommen", "cancelled", "expired", "declined", "abgelehnt"].includes(r.status) && !r.abgelaufen;
      if (filter === "offen" && !istOffen) return false;
      if (filter === "erledigt" && istOffen) return false;
      if (!q) return true;
      return `${r.kunde} ${r.zusatz} ${r.nummer} ${r.ansprechpartner}`.toLowerCase().includes(q);
    });
  }, [rows, suche, filter]);

  if (!rows) {
    return (
      <div className="py-10 flex justify-center" data-testid="angebot-uebersicht-loading">
        <Loader2 className="animate-spin text-[#BA6D52]" size={18} />
      </div>
    );
  }

  return (
    <div className="mt-12 border-t border-[#E3DECE] pt-8" data-testid="angebot-uebersicht">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Alle Angebote</p>
          <p className="text-sm text-[#757067] mt-1">
            {rows.length} Angebote aus allen drei Wegen — anklicken und direkt hier öffnen, als PDF
            laden oder erneut verschicken.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-[#E3DECE] bg-white">
            {[["alle", "Alle"], ["offen", "Offen"], ["erledigt", "Erledigt"]].map(([id, label]) => (
              <button key={id} onClick={() => setFilter(id)} data-testid={`angebot-filter-${id}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] transition-colors ${
                  filter === id ? "bg-[#2C2A26] text-white" : "text-[#757067] hover:text-[#2C2A26]"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A39C90]" />
            <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name oder Nummer"
              data-testid="angebot-uebersicht-suche"
              className="bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] pl-9 pr-3 py-2 text-sm w-[210px]" />
          </div>
        </div>
      </div>

      {sichtbar.length === 0 ? (
        <p className="py-10 text-center text-sm text-[#757067]" data-testid="angebot-uebersicht-leer">
          Keine Angebote gefunden.
        </p>
      ) : (
        <div className="divide-y divide-[#E3DECE] border-y border-[#E3DECE]" data-testid="angebot-uebersicht-liste">
          {sichtbar.map((r) => {
            const [quelleLabel, ziel] = QUELLE[r.quelle];
            const aktiv = offen === `${r.quelle}-${r.id}`;
            return (
              <div key={`${r.quelle}-${r.id}`}>
                <button onClick={() => setOffen(aktiv ? null : `${r.quelle}-${r.id}`)}
                  data-testid={`angebot-zeile-${r.id}`}
                  className={`w-full text-left py-3.5 px-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 transition-colors ${
                    aktiv ? "bg-[#FAF5F1]" : "hover:bg-[#FAF5F1]"}`}>
                  <span className="font-mono text-xs text-[#BA6D52] w-[88px] shrink-0">{r.nummer}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-[#2C2A26] truncate">{r.kunde}</span>
                    <span className="block text-xs text-[#757067] truncate">
                      {quelleLabel}{r.zusatz ? ` · ${r.zusatz}` : ""}
                      {r.datum ? ` · Einsatz ${deDate(r.datum)}` : ""}
                      {r.gueltig ? ` · gültig bis ${deDate(r.gueltig)}` : ""}
                    </span>
                  </span>
                  {r.betrag > 0 && (
                    <span className="font-mono text-sm text-[#2C2A26] w-[108px] text-right shrink-0">{eur(r.betrag)}</span>
                  )}
                  <span className="shrink-0"><Badge status={r.status} abgelaufen={r.abgelaufen} /></span>
                </button>

                {aktiv && (
                  <div className="bg-[#FAF5F1] px-2 pb-4" data-testid={`angebot-detail-${r.id}`}>
                    <div className="text-xs text-[#575249] mb-3 space-y-0.5">
                      {r.ansprechpartner && <p>Ansprechpartner: {r.ansprechpartner}</p>}
                      {r.email && <p>E-Mail: {r.email}</p>}
                      {r.ort && <p>Ort: {r.ort}</p>}
                      {r.personen > 0 && <p>Personen: {r.personen}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.link && (
                        <Aktion icon={ExternalLink} href={r.link} testId={`angebot-link-${r.id}`}>
                          Angebot öffnen
                        </Aktion>
                      )}
                      {r.quelle !== "stunden" && (
                        <>
                          <Aktion icon={FileDown} busy={busy} onClick={() => pdfOeffnen(r)}
                            testId={`angebot-pdf-${r.id}`}>PDF öffnen</Aktion>
                          <Aktion icon={MailCheck} busy={busy} onClick={() => kopieAnMich(r)}
                            testId={`angebot-kopie-${r.id}`}>Kopie an mich</Aktion>
                          <Aktion icon={Mail} busy={busy} onClick={() => anKundeSenden(r)}
                            testId={`angebot-senden-${r.id}`}>Erneut an Kunden</Aktion>
                        </>
                      )}
                      <Aktion icon={Pencil} onClick={() => onOpenTab?.(ziel)}
                        testId={`angebot-bearbeiten-${r.id}`}>Bearbeiten</Aktion>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AngebotUebersicht;
